import { useApproveBulkKPIMutation } from "@/app/features/employee/performance/performance.api";
import CustomDrawer from "@/components/CustomDrawer";
import useAuth from "@/hooks/useAuth";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { Button, Flex, Form } from "antd";
import TextArea from "antd/es/input/TextArea";

// ──────── Types ────────
interface BulkApproveKPIDrawerProps {
  open: boolean;
  onClose: () => void;
  mappingIds: string[];
  onSuccess?: () => void;
}

type FormType = {
  manager_comment?: string;
};

const BulkApproveKPIDrawer = ({
  open,
  onClose,
  mappingIds,
  onSuccess,
}: BulkApproveKPIDrawerProps) => {
  const { userId, organizationId } = useAuth();
  const [form] = Form.useForm<FormType>();
  const [approveBulkKPI, { isLoading }] = useApproveBulkKPIMutation();

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleConfirm = () => {
    form.validateFields().then(async (values) => {
      try {
        const res = await approveBulkKPI({
          mapping_ids: mappingIds,
          organization_id: organizationId || "",
          manager_id: userId || "",
          manager_comment: values.manager_comment,
        }).unwrap();

        if (res?.success === false) {
          toast("error", getErrorMessage(res, "Failed to approve KPIs"));
          return;
        }

        toast("success", "KPIs approved successfully");
        onSuccess?.();
        handleClose();
      } catch (error: any) {
        toast("error", getErrorMessage(error, "Failed to approve KPIs"));
      }
    });
  };

  return (
    <CustomDrawer
      open={open}
      onClose={handleClose}
      title="Approve KPIs"
    >
      <p className="text-theme-gray-400 text-sm mb-6">
        You are about to approve{" "}
        <span className="text-white font-semibold">{mappingIds.length}</span>{" "}
        selected KPI{mappingIds.length > 1 ? "s" : ""}. Add an optional comment
        before confirming.
      </p>

      <Form form={form} layout="vertical">
        <Form.Item label="Manager Comment" name="manager_comment">
          <TextArea rows={5} placeholder="Enter manager comment" />
        </Form.Item>

        <Flex justify="end" gap={12} className="mt-8">
          <Button
            size="large"
            htmlType="button"
            className="btn-primary-bordered min-w-24"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            size="large"
            htmlType="button"
            className="btn-primary min-w-24"
            onClick={handleConfirm}
            loading={isLoading}
          >
            Confirm
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
};

export default BulkApproveKPIDrawer;
